import { LazyLoader } from "@NextSkyTheme/lazy-load";

const RECONCILE_DELAY = 500;

/**
 * Prefetches and caches product gallery HTML per color option.
 * Integrates via window.MediaGalleryPrefetch hooks in variant-input.
 */
class MediaGalleryPrefetch {
  constructor() {
    this.cache = new Map();
    this.initializedInputs = new WeakSet();
    this.prefetchingKeys = new Set();
    this.appliedColorBySection = new Map();
    this.reconcileTimers = new Map();
    this.reconcileContext = new Map();
    this.fetchGeneration = new Map();
    this.cacheAppliedGeneration = new Map();
  }

  init(variantInput) {
    if (!variantInput?.hasAttribute("data-section-id")) return;
    if (!this.isEnabled(variantInput)) return;
    if (this.initializedInputs.has(variantInput)) return;

    this.initializedInputs.add(variantInput);
    this.cacheCurrentGallery(variantInput);
    this.schedulePrefetch(variantInput);
  }

  isEnabled(variantInput) {
    const sectionId = variantInput.getAttribute("data-section-id");
    const root = document.getElementById(`Product-${sectionId}`);
    return Boolean(root?.querySelector('[data-gallery-prefetch="true"]'));
  }

  isEnabledForSection(sectionId) {
    const root = document.getElementById(`Product-${sectionId}`);
    return Boolean(root?.querySelector('[data-gallery-prefetch="true"]'));
  }

  getGalleryBlock(sectionId) {
    return document
      .getElementById(`Product-${sectionId}`)
      ?.querySelector(".block__media-gallery");
  }

  getColorFieldset(variantInput) {
    return (
      variantInput.querySelector("ul.swatch-color")?.closest("fieldset") ||
      variantInput.querySelector(".options-color")?.closest("fieldset")
    );
  }

  getColorInputs(variantInput) {
    const fieldset = this.getColorFieldset(variantInput);
    if (!fieldset) return [];
    return Array.from(fieldset.querySelectorAll('input[type="radio"]'));
  }

  getSelectedColorValueId(variantInput) {
    const fieldset = this.getColorFieldset(variantInput);
    return fieldset?.querySelector('input[type="radio"]:checked')?.value || null;
  }

  getSelectedColorName(variantInput) {
    const checked = this.getColorFieldset(variantInput)?.querySelector(
      'input[type="radio"]:checked',
    );
    return checked?.getAttribute("data-option-value")?.trim() || null;
  }

  getColorNameForValueId(variantInput, colorValueId) {
    if (!colorValueId) return null;

    const input = this.getColorInputs(variantInput).find(
      (item) => item.value === colorValueId,
    );
    return input?.getAttribute("data-option-value")?.trim() || null;
  }

  getColorOptionNames(variantInput) {
    return this.getColorInputs(variantInput)
      .map((input) => input.getAttribute("data-option-value")?.trim())
      .filter(Boolean);
  }

  parseMediaAlt(alt) {
    if (!alt) {
      return { displayAlt: "", colors: [], rawAlt: "" };
    }

    const rawAlt = alt.trim();
    if (!rawAlt.includes("||")) {
      return { displayAlt: rawAlt, colors: [], rawAlt };
    }

    const altParts = rawAlt.split("||");
    const displayAlt = altParts[0]?.trim() || rawAlt;
    const colors = (altParts[1] || "")
      .split(",")
      .map((color) => color.trim())
      .filter(Boolean);

    return { displayAlt, colors, rawAlt };
  }

  altMatchesColor(alt, colorName) {
    if (!alt || !colorName) return false;

    const { rawAlt, colors } = this.parseMediaAlt(alt);
    const normalizedColor = colorName.toLowerCase();

    if (rawAlt.toLowerCase() === normalizedColor) {
      return true;
    }

    return colors.some(
      (color) => color.toLowerCase() === normalizedColor,
    );
  }

  resolveColorFromAlt(alt, colorNames) {
    if (!alt || !colorNames.length) return null;

    const { rawAlt, colors } = this.parseMediaAlt(alt);

    const exactMatch = colorNames.find(
      (name) => name.toLowerCase() === rawAlt.toLowerCase(),
    );
    if (exactMatch) return exactMatch;

    for (const color of colors) {
      const listMatch = colorNames.find(
        (name) => name.toLowerCase() === color.toLowerCase(),
      );
      if (listMatch) return listMatch;
    }

    return null;
  }

  getGalleryImages(container) {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        ".media-main-swiper .swiper-slide:not(.swiper-slide-duplicate) img[alt], .media-gallery__image img[alt], grid-gallery .grid-item img[alt], img[data-media-alt]",
      ),
    );
  }

  galleryHasForeignColorContent(container, variantInput, expectedColorName) {
    if (!container || !variantInput || !expectedColorName) return false;

    const colorNames = this.getColorOptionNames(variantInput);
    const normalizedExpected = expectedColorName.toLowerCase();

    for (const img of this.getGalleryImages(container)) {
      const rawAlt =
        img.getAttribute("data-media-alt") || img.getAttribute("alt");
      const resolvedColor = this.resolveColorFromAlt(rawAlt, colorNames);

      if (
        resolvedColor &&
        resolvedColor.toLowerCase() !== normalizedExpected
      ) {
        return true;
      }
    }

    return false;
  }

  buildPrefetchOptionValues(variantInput, targetColorValueId) {
    const colorValueIds = new Set(
      this.getColorInputs(variantInput).map((input) => input.value),
    );
    const checkedInputs = Array.from(
      variantInput.querySelectorAll('input[type="radio"]:checked'),
    );

    if (!checkedInputs.length) {
      return [targetColorValueId];
    }

    return checkedInputs.map((input) =>
      colorValueIds.has(input.value) ? targetColorValueId : input.value,
    );
  }

  getGalleryColorFromDom(sectionId, variantInput) {
    const gallery = this.getGalleryBlock(sectionId);
    if (!gallery || !variantInput) return null;

    const selectedColor = this.getSelectedColorName(variantInput);
    if (!selectedColor) return null;

    if (
      this.galleryHasForeignColorContent(gallery, variantInput, selectedColor)
    ) {
      return null;
    }

    const imgs = this.getGalleryImages(gallery);

    for (const img of imgs) {
      const rawAlt = img.getAttribute("data-media-alt") || img.getAttribute("alt");
      if (this.altMatchesColor(rawAlt, selectedColor)) {
        return selectedColor;
      }
    }

    for (const img of imgs) {
      const resolved = this.resolveColorFromAlt(
        img.getAttribute("data-media-alt") || img.getAttribute("alt"),
        this.getColorOptionNames(variantInput),
      );
      if (resolved) return resolved;
    }

    return null;
  }

  getPrimaryColorFromGalleryHtml(html, variantInput, expectedColorName) {
    if (!html || !variantInput || !expectedColorName) return null;

    const doc = new DOMParser().parseFromString(
      `<div>${html}</div>`,
      "text/html",
    );
    const imgs = doc.querySelectorAll("img[alt], img[data-media-alt]");

    for (const img of imgs) {
      const rawAlt =
        img.getAttribute("data-media-alt") || img.getAttribute("alt");
      if (this.altMatchesColor(rawAlt, expectedColorName)) {
        return expectedColorName;
      }
    }

    for (const img of imgs) {
      const resolved = this.resolveColorFromAlt(
        img.getAttribute("data-media-alt") || img.getAttribute("alt"),
        this.getColorOptionNames(variantInput),
      );
      if (resolved) return resolved;
    }

    return null;
  }

  doesCacheHtmlMatchColor(html, variantInput, colorValueId) {
    const expectedColorName = this.getColorNameForValueId(
      variantInput,
      colorValueId,
    );
    if (!expectedColorName || !html) return true;

    const doc = new DOMParser().parseFromString(
      `<div>${html}</div>`,
      "text/html",
    );
    const container = doc.body.firstElementChild;

    if (
      this.galleryHasForeignColorContent(
        container,
        variantInput,
        expectedColorName,
      )
    ) {
      return false;
    }

    const cachedColor = this.getPrimaryColorFromGalleryHtml(
      html,
      variantInput,
      expectedColorName,
    );

    if (!cachedColor) return true;

    return (
      expectedColorName.toLowerCase() === cachedColor.toLowerCase()
    );
  }

  isGalleryDomMatchingVariant(variantInput, sectionId) {
    const variantColor = this.getSelectedColorName(variantInput);
    const gallery = this.getGalleryBlock(sectionId);

    if (!variantColor) return true;
    if (!gallery) return false;

    if (
      this.galleryHasForeignColorContent(gallery, variantInput, variantColor)
    ) {
      return false;
    }

    const domColor = this.getGalleryColorFromDom(sectionId, variantInput);
    if (!domColor) return false;

    return variantColor.toLowerCase() === domColor.toLowerCase();
  }

  logVariantGalleryColors(variantInput, options = {}) {
    if (!this.isEnabled(variantInput)) return;

    const {
      source = "variant-change",
      cacheApplied = false,
      fetchContext = null,
    } = options;
    const sectionId = variantInput.getAttribute("data-section-id");
    const variantColorName = this.getSelectedColorName(variantInput);

    let galleryColorValueId = this.appliedColorBySection.get(sectionId);
    if (cacheApplied) {
      galleryColorValueId = this.appliedColorBySection.get(sectionId);
    } else if (fetchContext?.colorValueId) {
      galleryColorValueId = fetchContext.colorValueId;
    }

    const galleryColorExpected = this.getColorNameForValueId(
      variantInput,
      galleryColorValueId,
    );
    const galleryColorFromDom = this.getGalleryColorFromDom(
      sectionId,
      variantInput,
    );

    const isMatch =
      Boolean(variantColorName && galleryColorFromDom) &&
      variantColorName.toLowerCase() === galleryColorFromDom.toLowerCase();

    console.log("[GalleryPrefetch]", {
      source,
      variantColor: variantColorName,
      galleryColorExpected,
      galleryColorFromDom,
      match: isMatch,
    });
  }

  isColorChange(eventTarget) {
    if (!eventTarget) return false;
    return Boolean(
      eventTarget.closest("ul.swatch-color, .options-color, .swatch-color"),
    );
  }

  cacheKey(sectionId, colorValueId) {
    return `${sectionId}:${colorValueId}`;
  }

  beginVariantChange(variantInput) {
    const sectionId = variantInput.getAttribute("data-section-id");
    if (!sectionId || !this.isEnabled(variantInput)) return null;

    const generation = (this.fetchGeneration.get(sectionId) || 0) + 1;
    this.fetchGeneration.set(sectionId, generation);

    return {
      sectionId,
      generation,
      colorValueId: this.getSelectedColorValueId(variantInput),
    };
  }

  isStaleGalleryFetch(sectionId, fetchContext) {
    if (!this.isEnabledForSection(sectionId)) return false;
    if (!fetchContext) return true;

    const currentGeneration = this.fetchGeneration.get(sectionId);
    return fetchContext.generation !== currentGeneration;
  }

  getGalleryHtmlFromParsed(parsedHTML) {
    return parsedHTML.querySelector(".block__media-gallery")?.innerHTML || null;
  }

  isSameGalleryHtml(sectionId, parsedHTML) {
    const nextHtml = this.getGalleryHtmlFromParsed(parsedHTML);
    const currentHtml = this.getGalleryBlock(sectionId)?.innerHTML;
    return Boolean(nextHtml && nextHtml === currentHtml);
  }

  shouldSkipFetchGalleryUpdate(
    sectionId,
    parsedHTML,
    fetchContext,
    variantInput,
  ) {
    if (!fetchContext?.colorValueId) return false;

    const selectedColorId = this.getSelectedColorValueId(variantInput);
    if (selectedColorId !== fetchContext.colorValueId) return false;

    if (!this.isGalleryDomMatchingVariant(variantInput, sectionId)) {
      return false;
    }

    if (this.isSameGalleryHtml(sectionId, parsedHTML)) return true;

    const cacheApplied = this.cacheAppliedGeneration.get(sectionId);
    return Boolean(
      cacheApplied &&
        cacheApplied.generation === fetchContext.generation &&
        cacheApplied.colorValueId === fetchContext.colorValueId &&
        this.isGalleryDomMatchingVariant(variantInput, sectionId),
    );
  }

  handleFetchGalleryUpdate(sectionId, parsedHTML, fetchContext, variantInput) {
    if (!this.isEnabledForSection(sectionId)) return false;

    if (this.isStaleGalleryFetch(sectionId, fetchContext)) {
      return true;
    }

    if (
      this.shouldSkipFetchGalleryUpdate(
        sectionId,
        parsedHTML,
        fetchContext,
        variantInput,
      )
    ) {
      this.storeFromResponse(sectionId, parsedHTML, fetchContext, variantInput);
      const cacheApplied = this.cacheAppliedGeneration.get(sectionId);
      if (
        !(
          cacheApplied &&
          cacheApplied.generation === fetchContext.generation
        )
      ) {
        this.logVariantGalleryColors(variantInput, {
          source: "fetch-skip",
          fetchContext,
        });
      }
      return true;
    }

    const html = this.getGalleryHtmlFromParsed(parsedHTML);
    if (html && fetchContext?.colorValueId) {
      this.applyGalleryHtml(sectionId, html, fetchContext.colorValueId);
      this.scheduleReconcile(sectionId, variantInput);
      this.logVariantGalleryColors(variantInput, {
        source: "fetch-apply",
        fetchContext,
      });
    }

    this.storeFromResponse(sectionId, parsedHTML, fetchContext, variantInput);
    return true;
  }

  cacheCurrentGallery(variantInput) {
    const sectionId = variantInput.getAttribute("data-section-id");
    const colorValueId = this.getSelectedColorValueId(variantInput);
    const destination = this.getGalleryBlock(sectionId);

    if (!sectionId || !colorValueId || !destination?.innerHTML) return;

    this.cache.set(
      this.cacheKey(sectionId, colorValueId),
      destination.innerHTML,
    );
    this.appliedColorBySection.set(sectionId, colorValueId);
  }

  activateGalleryLazyLoad(sectionId) {
    const gallery = this.getGalleryBlock(sectionId);
    if (!gallery) return;

    gallery.querySelectorAll(".image-lazy-load").forEach((imgElement) => {
      if (
        imgElement.classList.contains("lazy-loaded") &&
        imgElement.getAttribute("srcset")
      ) {
        return;
      }

      const pictureElement = imgElement.closest("picture");
      if (pictureElement) {
        const sourceElement = pictureElement.querySelector("source");
        const imgSourceSrcset = sourceElement?.dataset?.srcset;
        if (imgSourceSrcset) {
          sourceElement.setAttribute("srcset", imgSourceSrcset);
          sourceElement.removeAttribute("data-srcset");
        }
      }

      const imgSrcset = imgElement.dataset.srcset;
      if (imgSrcset) {
        imgElement.setAttribute("srcset", imgSrcset);
        imgElement.removeAttribute("data-srcset");
        imgElement.classList.remove("image-lazy-load");
        imgElement.classList.add("lazy-loaded");
      }
    });

    const pendingImages = gallery.querySelectorAll(
      ".image-lazy-load:not(.lazy-loaded)",
    );
    if (pendingImages.length) {
      new LazyLoader(
        `#Product-${sectionId} .block__media-gallery .image-lazy-load:not(.lazy-loaded)`,
      );
    }
  }

  applyGalleryHtml(sectionId, html, colorValueId) {
    const destination = this.getGalleryBlock(sectionId);
    if (!destination || !html) return false;

    destination.classList.add("insert");
    destination.innerHTML = html;
    this.activateGalleryLazyLoad(sectionId);
    this.appliedColorBySection.set(sectionId, colorValueId);
    return true;
  }

  applyFromCache(variantInput, eventTarget) {
    if (!this.isEnabled(variantInput)) return false;
    if (!this.isColorChange(eventTarget)) return false;

    const sectionId = variantInput.getAttribute("data-section-id");
    const colorValueId = this.getSelectedColorValueId(variantInput);
    const cacheKey = this.cacheKey(sectionId, colorValueId);
    const html = this.cache.get(cacheKey);

    if (!html) return false;

    if (!this.doesCacheHtmlMatchColor(html, variantInput, colorValueId)) {
      this.cache.delete(cacheKey);
      return false;
    }

    if (!this.applyGalleryHtml(sectionId, html, colorValueId)) return false;

    if (!this.isGalleryDomMatchingVariant(variantInput, sectionId)) {
      this.cache.delete(cacheKey);
      return false;
    }

    this.cacheAppliedGeneration.set(sectionId, {
      generation: this.fetchGeneration.get(sectionId),
      colorValueId,
    });
    this.scheduleReconcile(sectionId, variantInput);
    return true;
  }

  scheduleReconcile(sectionId, variantInput) {
    const existingTimer = this.reconcileTimers.get(sectionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    this.reconcileContext.set(sectionId, variantInput);

    const timerId = setTimeout(() => {
      this.reconcileTimers.delete(sectionId);
      const contextInput = this.reconcileContext.get(sectionId);
      if (!contextInput) return;
      this.reconcileGallery(sectionId, contextInput);
    }, RECONCILE_DELAY);

    this.reconcileTimers.set(sectionId, timerId);
  }

  reconcileGallery(sectionId, variantInput) {
    if (!this.isEnabled(variantInput)) return;

    const expectedColorId = this.getSelectedColorValueId(variantInput);
    const appliedColorId = this.appliedColorBySection.get(sectionId);

    if (!expectedColorId) return;

    if (appliedColorId !== expectedColorId) {
      const html = this.cache.get(this.cacheKey(sectionId, expectedColorId));
      if (html && this.doesCacheHtmlMatchColor(html, variantInput, expectedColorId)) {
        this.applyGalleryHtml(sectionId, html, expectedColorId);
      }
    } else if (!this.isGalleryDomMatchingVariant(variantInput, sectionId)) {
      const html = this.cache.get(this.cacheKey(sectionId, expectedColorId));
      if (html && this.doesCacheHtmlMatchColor(html, variantInput, expectedColorId)) {
        this.applyGalleryHtml(sectionId, html, expectedColorId);
      }
    }

    const mediaGallery = this.getGalleryBlock(sectionId)?.querySelector(
      "media-gallery",
    );
    if (!mediaGallery) return;

    this.syncGallerySliders(mediaGallery);

    requestAnimationFrame(() => {
      this.syncGallerySliders(mediaGallery);
    });
  }

  syncGallerySliders(mediaGallery) {
    const swiperElements = mediaGallery.querySelectorAll(
      ".swiper-wrapper-main, .swiper-wrapper-thumbnail",
    );

    swiperElements.forEach((element) => {
      const swiper = element.swiper;
      if (!swiper?.initialized) return;

      swiper.update();

      if (swiper.params.loop && typeof swiper.slideToLoop === "function") {
        swiper.slideToLoop(0, 0);
      } else {
        swiper.slideTo(0, 0);
      }
    });

    const mainSwiper = mediaGallery.querySelector(
      ".media-main-swiper .swiper-wrapper-main",
    )?.swiper;
    const thumbSwiper = mediaGallery.querySelector(
      ".media-thumb-swiper .swiper-wrapper-thumbnail",
    )?.swiper;

    if (mainSwiper?.thumbs && thumbSwiper?.initialized) {
      mainSwiper.thumbs.swiper = thumbSwiper;
      if (typeof mainSwiper.thumbs.update === "function") {
        mainSwiper.thumbs.update(true);
      }
    }

    const mainElement = mediaGallery.querySelector(
      ".media-main-swiper .swiper-wrapper-main",
    );
    if (
      mainElement &&
      !mainElement.swiper &&
      typeof mediaGallery.init === "function"
    ) {
      mediaGallery.init();
    }
  }

  storeFromResponse(sectionId, parsedHTML, fetchContext, variantInput) {
    if (!sectionId || !parsedHTML || !fetchContext?.colorValueId) return;
    if (this.isStaleGalleryFetch(sectionId, fetchContext)) return;

    const html = this.getGalleryHtmlFromParsed(parsedHTML);
    if (!html) return;

    if (
      variantInput &&
      !this.doesCacheHtmlMatchColor(
        html,
        variantInput,
        fetchContext.colorValueId,
      )
    ) {
      return;
    }

    this.cache.set(
      this.cacheKey(sectionId, fetchContext.colorValueId),
      html,
    );
    this.appliedColorBySection.set(sectionId, fetchContext.colorValueId);
  }

  schedulePrefetch(variantInput) {
    const run = () => this.prefetchAllColors(variantInput);

    if ("requestIdleCallback" in window) {
      requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1500);
    }
  }

  async prefetchAllColors(variantInput) {
    const sectionId = variantInput.getAttribute("data-section-id");
    const productUrl = variantInput.getAttribute("data-product-url");
    const colorInputs = this.getColorInputs(variantInput);

    if (!sectionId || !productUrl || !colorInputs.length) return;

    for (const input of colorInputs) {
      const key = this.cacheKey(sectionId, input.value);
      if (this.cache.has(key) || this.prefetchingKeys.has(key)) continue;

      this.prefetchingKeys.add(key);
      try {
        const html = await this.fetchGalleryHtml(
          productUrl,
          sectionId,
          input.value,
          variantInput,
        );
        if (
          html &&
          this.doesCacheHtmlMatchColor(html, variantInput, input.value)
        ) {
          this.cache.set(key, html);
        }
      } catch (error) {
        console.warn("Gallery prefetch failed:", error);
      } finally {
        this.prefetchingKeys.delete(key);
      }

      await this.delay(150);
    }
  }

  async fetchGalleryHtml(productUrl, sectionId, colorValueId, variantInput) {
    const baseUrl = productUrl.split("?")[0];
    const optionValues = variantInput
      ? this.buildPrefetchOptionValues(variantInput, colorValueId)
      : [colorValueId];
    const url = `${baseUrl}?section_id=${sectionId}&option_values=${optionValues.join(",")}`;
    const response = await fetch(url);

    if (!response.ok) return null;

    const responseText = await response.text();
    const parsedHTML = new DOMParser().parseFromString(
      responseText,
      "text/html",
    );

    return parsedHTML.querySelector(".block__media-gallery")?.innerHTML || null;
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  bootExisting() {
    document.querySelectorAll("variant-input[data-section-id]").forEach(
      (variantInput) => {
        this.init(variantInput);
      },
    );
  }
}

window.MediaGalleryPrefetch = new MediaGalleryPrefetch();
window.MediaGalleryPrefetch.bootExisting();
